import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router"
import { toastSuccess } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { useServerTable } from "../../hooks/useServerTable"
import { EyeIcon, PencilIcon } from "../../icons"

type Program = {
  id: string
  name: string
  slug: string
  description?: string | null
  shortDescription?: string | null
  isPublished: boolean
  orderIndex: number
  course_count?: number
}

export default function ProgramsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [search, setSearch] = useState("")
  const [publishedFilter, setPublishedFilter] = useState<"" | "published" | "draft">("")  const flashSuccess = (location.state as { success?: string } | null)?.success ?? ""

  useEffect(() => {
    if (flashSuccess) toastSuccess(flashSuccess)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    rows: programs,
    total,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    error,
    reload,
  } = useServerTable<Program>({
    endpoint: "/academy/programs/admin/all",
    defaultPageSize: 20,
    defaultSort: { column: "orderIndex", dir: "asc" },
    // El estado viaja al servidor: el total y la paginación son reales con cualquier cantidad de rutas.
    params: { search, isPublished: publishedFilter === "published" ? "true" : publishedFilter === "draft" ? "false" : undefined },
    extractRows: (data) => data.programs ?? [],
    extractTotal: (data) => data.pagination?.total ?? 0,
  })

  const withCourses = programs.filter((p) => (p.course_count ?? 0) > 0).length
  const withoutCourses = programs.length - withCourses

  const columns: DataTableColumn<Program>[] = [
    {
      key: "name",
      label: "Ruta",
      sortable: true,
      render: (program) => (
        <>
          <p className="font-semibold text-gray-900 dark:text-white">{program.name}</p>
          <code className="text-[11px] text-gray-500 dark:text-gray-400">{program.slug}</code>
        </>
      ),
    },
    {
      key: "description",
      label: "Descripción",
      render: (program) => (
        <span className="text-xs text-gray-500 line-clamp-2">{program.shortDescription ?? "—"}</span>
      ),
    },
    {
      key: "isPublished",
      label: "Estado",
      align: "center",
      render: (program) => (
        <span
          className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
            program.isPublished
              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
              : "bg-gray-100 text-gray-500 dark:bg-gray-800"
          }`}
        >
          {program.isPublished ? "Publicada" : "Borrador"}
        </span>
      ),
    },
    {
      key: "course_count",
      label: "Cursos",
      align: "center",
      render: (program) => (
        <span className="text-gray-600 dark:text-gray-400">{program.course_count ?? 0}</span>
      ),
    },
    {
      key: "actions",
      label: "Acciones",
      align: "right",
      render: (program) => (
        <RowActionsMenu
          actions={[
            { label: "Ver programa", icon: <EyeIcon />, to: `/programs/programs/${program.id}` },
            { label: "Editar", icon: <PencilIcon />, to: `/programs/programs/${program.id}/edit`, requires: "academy:programs:edit" },
          ]}
        />
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Programas", "Rutas académicas"]}
      title="Rutas académicas"
      subtitle="Programas de formación del proyecto SateLab"
      tabs={[
        { label: "Rutas", to: "/programs/programs", active: true },
        { label: "Organizaciones", to: "/programs/institutions", active: false },
        { label: "Semilleros", to: "/programs/seedbeds", active: false },
      ]}
      metrics={[
        { label: "Total", value: String(total) },
        {
          label: "Con cursos (esta página)",
          value: String(withCourses),
          tone: "good",
          hint: "Listas para inscribir estudiantes — solo cuenta las filas visibles, no el total",
        },
        {
          label: "Sin cursos (esta página)",
          value: String(withoutCourses),
          tone: withoutCourses > 0 ? "warning" : "neutral",
          hint: "Vacías: no ofrecen contenido todavía — solo cuenta las filas visibles, no el total",
        },
      ]}
      toolbar={{
        search: {
          value: search,
          onChange: setSearch,
          placeholder: "Buscar ruta...",
        },
        filters: [
          {
            type: "select",
            key: "published",
            label: "Estado",
            value: publishedFilter,
            onChange: (v) => setPublishedFilter(v as "" | "published" | "draft"),
            options: [
              { label: "Todos", value: "" },
              { label: "Publicada", value: "published" },
              { label: "Borrador", value: "draft" },
            ],
          },
        ],
        onRefresh: reload,
        refreshing: loading,        onCreate: () => navigate("/programs/programs/new"),
        createLabel: "+ Nueva ruta",
        requires: "academy:programs:edit",
      }}
    >
      {error && (
        <p
          role="alert"
          aria-live="assertive"
          className="rounded-xl border border-error-200 bg-error-50 px-4 py-2 text-sm text-error-600 dark:border-error-500/20 dark:bg-error-500/10 dark:text-error-400 mb-4"
        >
          {error}
        </p>
      )}

      <DataTable
        columns={columns}
        rows={programs}
        rowKey={(program) => program.id}
        loading={loading}
        emptyMessage="No se encontraron rutas académicas."
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={total}
        onPageChange={setPage}
      />    </ModuleLayout>
  )
}
